import {
  useInvalidate,
  useTranslate,
  Authenticated,
  CanAccess,
  useGo,
} from "@refinedev/core";
import { Button, Space, Table, TableColumnsType } from "antd";
import { type FaceProfileResponse } from "../types";
import { useOrganization } from "hooks/useOrganization";
import { Uuid } from "../../../components/UUID";
import { PlusOutlined, SyncOutlined } from "@ant-design/icons";
import { CreateButton, DateField, useTable, List } from "@refinedev/antd";
import { FaceProfilePreview } from "../components/FaceProfilePreview";
import { addDateTz } from "../utils";
import { DeleteButtonWithErrorPopover } from "components/DeleteButtonWithErrorPopover/";

const FaceProfilesListPage = () => {
  const t = useTranslate();
  const { organization } = useOrganization({});
  const go = useGo();
  const resource = `media/${organization?.id}/face_profiles`;
  const invalidate = useInvalidate();

  const {
    tableQueryResult: { data, isLoading, refetch, isRefetching },
    tableProps,
  } = useTable<FaceProfileResponse>({
    resource,
  });

  const columns: TableColumnsType<FaceProfileResponse> = [
    {
      title: t("media.faces.index.id"),
      dataIndex: "id",
      key: "id",
      render: (id: string) => <Uuid id={id} />,
    },
    {
      title: t("media.faces.index.name"),
      dataIndex: "name",
      key: "name",
    },
    {
      title: t("media.faces.index.type"),
      dataIndex: "category",
      key: "category",
    },
    {
      title: t("media.faces.index.preview"),
      dataIndex: "extracted_asset_id",
      key: "extracted_asset_id",
      render: (_value: string, record: FaceProfileResponse) => {
        return (
          <FaceProfilePreview
            assetPath={
              record.thumbnail_asset_path ??
              record.extracted_asset_path ??
              record.custom_asset_path
            }
            name={record.name}
            style={{
              height: 100,
              width: 100,
            }}
          />
        );
      },
    },
    {
      title: t("media.faces.index.creationDate"),
      dataIndex: "created",
      key: "created",
      render: (text) => <DateField value={addDateTz(text)} format="LLL" />,
    },
    {
      title: t("media.faces.index.actions"),
      key: "actions",
      render: (_, record: FaceProfileResponse) => (
        <CanAccess resource="media_faces" action="delete">
          <Space>
            <DeleteButtonWithErrorPopover
              size="small"
              shape="round"
              resource={`media/face_profiles`}
              recordItemId={record.id}
              accessControl={{ enabled: false }}
              onSuccess={() => {
                invalidate({
                  resource,
                  invalidates: ["list"],
                }).catch((error) => {
                  console.error("Failed to invalidate faces list", error);
                });
              }}
            />
          </Space>
        </CanAccess>
      ),
    },
  ];

  return (
    <Authenticated key="face_list">
      <List
        headerButtons={({ createButtonProps }) => (
          <Space>
            <Button
              icon={<SyncOutlined />}
              onClick={() => refetch()}
              disabled={isRefetching}
              shape="circle"
              type="text"
            />
            {/* todo check accesses */}
            {createButtonProps && (
              <CreateButton
                {...createButtonProps}
                icon={<PlusOutlined />}
                shape="round"
              >
                {t("media.faces.index.createAFace")}
              </CreateButton>
            )}
          </Space>
        )}
      >
        <Table<FaceProfileResponse>
          loading={isLoading}
          dataSource={data?.data}
          columns={columns}
          onChange={tableProps.onChange}
          pagination={{
            ...tableProps.pagination,
          }}
          onRow={(record) => ({
            onClick: (event) => {
              const target = event.target as HTMLElement;
              if (!target.closest(".ant-btn")) {
                go({
                  to: {
                    resource: "media_faces",
                    id: record.id,
                    action: "show",
                  },
                });
              }
            },
            style: { cursor: "pointer" },
          })}
        />
      </List>
    </Authenticated>
  );
};

export default FaceProfilesListPage;
